import { useCallback, useEffect, useMemo, useState } from 'react';
import { GridLayoutsApiService } from '~/services';
import { NotifyFailure, NotifySuccess } from '~/utils';
import {
  getKnownColumnIdentifiers,
  parseLayoutData,
  prepareLayoutStateForSave,
  sanitizeLayoutStateForApply,
} from './grid-layout-state';
import { buildLayoutExport } from './grid-layout-transfer';

// Per-tab, per-grid. sessionStorage rather than localStorage is deliberate: two tabs open on the
// same grid are usually open *because* the user wants two different views of it, so the sticky state
// should not follow them between tabs. What follows the user between machines is the IsDefault
// layout, which lives on the server.
const SESSION_KEY_PREFIX = 'squire.gridLayout.';

// sessionStorage throws rather than returning null in some privacy modes, and sticky state is a
// convenience — never a reason to stop a grid from mounting.
function readSessionState(sessionKey) {
  if (!sessionKey) {
    return null;
  }

  try {
    return parseLayoutData(sessionStorage.getItem(sessionKey));
  } catch {
    return null;
  }
}

function writeSessionState(sessionKey, state) {
  if (!sessionKey) {
    return;
  }

  try {
    if (state) {
      sessionStorage.setItem(sessionKey, JSON.stringify(state));
    } else {
      sessionStorage.removeItem(sessionKey);
    }
  } catch {
    // Storage unavailable. The grid still works; the view just will not be sticky.
  }
}

// The list endpoint is server-mode, so the store may resolve a bare array or a LoadResult, and the
// API serialises with ReferenceHandler.Preserve so a collection can arrive wrapped in $values.
function toLayoutArray(loaded) {
  if (Array.isArray(loaded)) {
    return loaded;
  }

  const data = loaded?.data ?? loaded;
  if (Array.isArray(data)) {
    return data;
  }

  return Array.isArray(data?.$values) ? data.$values : [];
}

/**
 * Saved grid layouts for one grid. Opt in by passing a `layoutKey` from `~/static-data`'s
 * gridLayoutKeys; without one the hook is inert and nothing is fetched, which is what keeps grids
 * inside editors, tabs and popups unaffected.
 *
 * The hook owns the `stateStoring type="custom"` contract. Two different things are being persisted
 * and it is worth keeping them straight:
 *
 *   * `customSave` writes to **sessionStorage** on every change DevExtreme reports. That is the
 *     sticky per-tab view, and it never touches the server.
 *   * The **named layouts** are explicit user actions (saveLayoutAs / updateActiveLayout) and are the
 *     only thing that reaches the API.
 *
 * `customLoad` picks session state first, then the user's IsDefault layout, then nothing — leaving
 * the grid on its declared configuration.
 */
export function useGridLayout(layoutKey, gridRef, { enableColumnChooser = false } = {}) {
  const isEnabled = Boolean(layoutKey);

  // Constructed here rather than at module scope: SquireDataGrid is imported by every page and by
  // the ~/components barrel, and a module-scope service would be built on import in all of them.
  const apiService = useMemo(() => (isEnabled ? new GridLayoutsApiService() : null), [isEnabled]);

  const sessionKey = isEnabled ? `${SESSION_KEY_PREFIX}${layoutKey}` : null;

  const [layouts, setLayouts] = useState([]);
  const [activeLayout, setActiveLayout] = useState(null);

  const getGridInstance = useCallback(() => gridRef?.current?.instance?.() ?? null, [gridRef]);

  const readPreparedState = useCallback(() => {
    const instance = getGridInstance();
    const state = instance ? instance.state() : null;
    return prepareLayoutStateForSave(state, { includeColumnVisibility: enableColumnChooser });
  }, [enableColumnChooser, getGridInstance]);

  // A fresh store each time rather than one held across renders: the list has to reflect a save or
  // delete that just happened, and a retained store would serve its cached page.
  const refreshLayouts = useCallback(async () => {
    if (!apiService) {
      return;
    }

    try {
      const loaded = await apiService.getGridLayoutsDataSource(layoutKey).load();
      setLayouts(toLayoutArray(loaded));
    } catch {
      setLayouts([]);
    }
  }, [apiService, layoutKey]);

  useEffect(() => {
    refreshLayouts();
  }, [refreshLayouts]);

  // Runs while the grid is mounting, before it has columns, so the sanitiser is given no known
  // fields and drops nothing. Stale columns are reported on an explicit applyLayout instead, where
  // the grid can actually say what it has.
  const customLoad = useCallback(async () => {
    const sessionState = readSessionState(sessionKey);
    if (sessionState) {
      return sanitizeLayoutStateForApply(sessionState).state ?? undefined;
    }

    if (!apiService) {
      return undefined;
    }

    try {
      const defaultLayout = await apiService.getDefaultGridLayout(layoutKey);
      const { state } = sanitizeLayoutStateForApply(defaultLayout?.data);
      if (!state) {
        return undefined;
      }

      setActiveLayout({ id: defaultLayout.id, name: defaultLayout.name });
      return state;
    } catch {
      // A failed request must not reject inside DevExtreme's mount path.
      return undefined;
    }
  }, [apiService, layoutKey, sessionKey]);

  const customSave = useCallback(
    (state) => {
      writeSessionState(sessionKey, prepareLayoutStateForSave(state, { includeColumnVisibility: enableColumnChooser }));
    },
    [enableColumnChooser, sessionKey]
  );

  const stateStoring = useMemo(
    () => (isEnabled ? { enabled: true, type: 'custom', customLoad, customSave } : null),
    [customLoad, customSave, isEnabled]
  );

  const applyLayout = useCallback(
    async (gridLayoutId) => {
      if (!apiService) {
        return;
      }

      let layout = null;
      try {
        layout = await apiService.getGridLayout(gridLayoutId);
      } catch {
        layout = null;
      }

      const instance = getGridInstance();
      const { state, droppedColumns } = sanitizeLayoutStateForApply(
        layout?.data,
        getKnownColumnIdentifiers(instance)
      );

      if (!state) {
        NotifyFailure({ message: 'That layout could not be loaded.' });
        return;
      }

      instance?.state(state);
      setActiveLayout({ id: layout.id, name: layout.name });

      if (droppedColumns.length > 0) {
        NotifyFailure({
          message:
            `Applied "${layout.name}", but these columns no longer exist and were ignored: ` +
            `${droppedColumns.join(', ')}.`,
        });
      }
    },
    [apiService, getGridInstance]
  );

  // Back to the grid's own declared configuration: state(null) is DevExtreme's reset.
  const resetLayout = useCallback(() => {
    writeSessionState(sessionKey, null);
    getGridInstance()?.state(null);
    setActiveLayout(null);
  }, [getGridInstance, sessionKey]);

  const saveLayoutAs = useCallback(
    async (name) => {
      if (!apiService) {
        return undefined;
      }

      // Deliberately not caught: a duplicate name comes back as a rejection carrying the domain's
      // message, and the caller (the name popup) needs it to keep itself open.
      const newId = await apiService.createGridLayout(layoutKey, name, JSON.stringify(readPreparedState() ?? {}));

      setActiveLayout({ id: newId, name });
      NotifySuccess({ message: `Saved layout "${name}".` });
      await refreshLayouts();
      return newId;
    },
    [apiService, layoutKey, readPreparedState, refreshLayouts]
  );

  const updateActiveLayout = useCallback(async () => {
    if (!apiService || !activeLayout) {
      return;
    }

    // Name omitted, so the server leaves it alone.
    await apiService.updateGridLayout(activeLayout.id, { data: JSON.stringify(readPreparedState() ?? {}) });
    NotifySuccess({ message: `Updated layout "${activeLayout.name}".` });
  }, [activeLayout, apiService, readPreparedState]);

  const renameLayout = useCallback(
    async (gridLayoutId, name) => {
      if (!apiService) {
        return;
      }

      // Data omitted, so a rename cannot overwrite the saved view.
      await apiService.updateGridLayout(gridLayoutId, { name });

      setActiveLayout((current) => (current?.id === gridLayoutId ? { ...current, name } : current));
      NotifySuccess({ message: `Renamed layout to "${name}".` });
      await refreshLayouts();
    },
    [apiService, refreshLayouts]
  );

  const setDefaultLayout = useCallback(
    async (gridLayoutId, isDefault) => {
      if (!apiService) {
        return;
      }

      // One call: demoting whatever was default is the entity's job.
      await apiService.setGridLayoutDefault(gridLayoutId, isDefault);
      await refreshLayouts();
    },
    [apiService, refreshLayouts]
  );

  const deleteLayout = useCallback(
    async (gridLayoutId) => {
      if (!apiService) {
        return;
      }

      await apiService.deleteGridLayout(gridLayoutId);
      setActiveLayout((current) => (current?.id === gridLayoutId ? null : current));
      await refreshLayouts();
    },
    [apiService, refreshLayouts]
  );

  /**
   * Builds the export file for the active layout and hands it back — writing it to disk is the
   * caller's job, so the hook stays clear of the DOM.
   *
   * The **saved** layout is exported, not the grid's current state: Export sits with Update, Rename
   * and Delete, which are all scoped to the applied layout. Someone who has tweaked the view since
   * saving would otherwise send a file that does not match the layout the menu says is applied.
   */
  const buildActiveLayoutExport = useCallback(async () => {
    if (!apiService || !activeLayout) {
      throw new Error('There is no layout to export.');
    }

    // Only the list projection is held in state; the state blob itself lives on the details endpoint.
    const layout = await apiService.getGridLayout(activeLayout.id);
    if (!layout) {
      // fetchApi swallows a 4xx, so a layout deleted in another tab arrives as a nullish value.
      throw new Error('That layout could not be loaded.');
    }

    return buildLayoutExport(layout);
  }, [activeLayout, apiService]);

  /**
   * Stores a layout read out of a file, then applies it.
   *
   * Both keys go to the server, which refuses a mismatch — that refusal is the boundary. The toolbar
   * checks first only so a wrong file fails without a round trip.
   *
   * Not caught, exactly like saveLayoutAs: a duplicate name is a rejection carrying the domain's
   * message and the import popup needs it to stay open.
   */
  const importLayout = useCallback(
    async (fileLayoutKey, name, data) => {
      if (!apiService) {
        return undefined;
      }

      const newId = await apiService.importGridLayout(layoutKey, fileLayoutKey, name, data);

      NotifySuccess({ message: `Imported layout "${name}".` });
      await refreshLayouts();

      // Applied rather than just listed, so the user sees what they were sent. This also runs the
      // stale-column check, which is how a file from an older build reports what it lost.
      await applyLayout(newId);

      return newId;
    },
    [apiService, applyLayout, layoutKey, refreshLayouts]
  );

  // Memoised so the object identity changes only when the layout list or the applied layout
  // actually change. SquireDataGrid feeds this straight into a toolbar item's memoised render, and
  // a fresh object every render would churn DevExtreme's deferred toolbar rendering.
  return useMemo(
    () => ({
      isEnabled,
      // Exposed so the toolbar can check a picked file's key before opening the name popup.
      layoutKey,
      stateStoring,
      layouts,
      activeLayout,
      refreshLayouts,
      applyLayout,
      resetLayout,
      saveLayoutAs,
      updateActiveLayout,
      renameLayout,
      setDefaultLayout,
      deleteLayout,
      buildActiveLayoutExport,
      importLayout,
    }),
    [
      activeLayout,
      applyLayout,
      buildActiveLayoutExport,
      deleteLayout,
      importLayout,
      isEnabled,
      layoutKey,
      layouts,
      refreshLayouts,
      renameLayout,
      resetLayout,
      saveLayoutAs,
      setDefaultLayout,
      stateStoring,
      updateActiveLayout,
    ]
  );
}
